import {
  APP_SHELL_ROUTES,
  COMPATIBILITY_SHELL_ROUTES,
  DYNAMIC_TEXT_ROUTES,
  STATIC_DOCUMENT_ROUTES,
  type PublicRouteSpec,
} from './route-catalog';
import type { PublicPageId, PublicRouteMatch } from './types';

const SUPPORTED_LANGS = new Set([
  'en',
  'de',
  'fr',
  'es',
  'it',
  'pt',
  'pl',
  'cs',
  'hu',
  'ro',
  'tr',
  'uk',
  'ru',
]);

type RoutePattern = PublicRouteSpec & {
  match: (segments: string[]) => Record<string, string> | null;
};

const ROUTES: RoutePattern[] = [
  ...APP_SHELL_ROUTES.map((route) => ({ ...route, match: path(...route.slug.split('/')) })),
  ...DYNAMIC_TEXT_ROUTES.map((route) => ({
    ...route,
    match: route.routeRole === 'text-index' ? path('t') : dynamicText,
  })),
];

const STATIC_DOCUMENTS = new Map(STATIC_DOCUMENT_ROUTES.map((route) => [route.slug, route]));

export function matchPublicRoute(
  pathname: string = window.location.pathname,
  hash: string = window.location.hash,
): PublicRouteMatch {
  const normalized = normalizePathname(pathname);
  const parsed = stripLanguagePrefix(normalized);
  const routeSegments = parsed.segments;

  for (const route of ROUTES) {
    const params = route.match(routeSegments);
    if (!params) continue;
    const canonicalPath = buildCanonicalPath(parsed.lang, route.slug, params);
    return buildRouteMatch({
      id: route.id,
      route,
      pathname: normalized,
      lang: parsed.lang,
      params,
      hashMode:
        route.id === 'space-text' &&
        (route as PublicRouteSpec & { allowHashManage?: boolean }).allowHashManage === true &&
        hasManageHash(hash)
          ? 'manage'
          : 'none',
      canonicalPath,
    });
  }

  const slug = routeSegments.join('/');
  const staticRoute = STATIC_DOCUMENTS.get(slug);
  if (staticRoute) {
    return buildRouteMatch({
      id: staticRoute.id,
      route: staticRoute,
      pathname: normalized,
      lang: parsed.lang,
      params: {},
      hashMode: 'none',
      canonicalPath: buildCanonicalPath(parsed.lang, staticRoute.slug, {}),
    });
  }

  return {
    id: 'unknown',
    kind: 'unknown',
    owner: 'unknown',
    pathname: normalized,
    lang: parsed.lang,
    slug,
    params: {},
    hashMode: 'none',
    canonicalPath: normalized,
    jsRequiredForPrimaryContent: true,
    cachePolicy: { cacheControl: 'no-store', mayCacheAtCdn: false, private: true },
    directlyOpenable: true,
    clientRouterMayEnhance: false,
    clientRouterMayBeSourceOfTruth: false,
  };
}

export function hasManageHash(hash: string): boolean {
  const raw = String(hash || '').replace(/^#/, '');
  if (!raw) return false;
  if (raw === 'manage' || raw.startsWith('manage&') || raw.startsWith('manage=')) {
    return true;
  }
  const params = new URLSearchParams(raw);
  return params.has('manage') || params.has('token');
}

export function normalizePathname(pathname: string): string {
  const clean = `/${String(pathname || '').replace(/^\/+/, '')}`;
  return clean
    .replace(/\/index\.html$/i, '/')
    .replace(/\.html$/i, '')
    .replace(/\/+/g, '/');
}

function buildRouteMatch(input: {
  id: PublicPageId;
  route: PublicRouteSpec;
  pathname: string;
  lang: string;
  params: Record<string, string>;
  hashMode: 'none' | 'manage';
  canonicalPath: string;
}): PublicRouteMatch {
  return {
    id: input.id,
    kind: input.route.kind,
    owner: input.route.owner,
    pathname: input.pathname,
    lang: input.lang,
    slug: input.route.slug,
    params: input.params,
    hashMode: input.hashMode,
    canonicalPath: input.canonicalPath,
    jsRequiredForPrimaryContent: input.route.jsRequiredForPrimaryContent,
    cachePolicy: input.route.cachePolicy,
    directlyOpenable: input.route.directlyOpenable,
    clientRouterMayEnhance: input.route.clientRouterMayEnhance,
    clientRouterMayBeSourceOfTruth: input.route.clientRouterMayBeSourceOfTruth,
  };
}

function stripLanguagePrefix(pathname: string): { lang: string; segments: string[] } {
  const segments = pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));
  if (segments.length && SUPPORTED_LANGS.has(segments[0])) {
    return { lang: segments[0], segments: segments.slice(1) };
  }
  return { lang: 'en', segments };
}

function path(...expected: string[]): (segments: string[]) => Record<string, string> | null {
  return (segments) => {
    if (segments.length !== expected.length) return null;
    return expected.every((part, index) => segments[index] === part) ? {} : null;
  };
}

function dynamicText(segments: string[]): Record<string, string> | null {
  if (segments.length !== 2 || segments[0] !== 't' || !segments[1]) return null;
  return { publicId: segments[1] };
}

function buildCanonicalPath(lang: string, slug: string, params: Record<string, string>): string {
  const localizedPrefix = lang && lang !== 'en' ? `/${lang}` : '';
  if (!slug) return `${localizedPrefix || ''}/`;
  const resolved = slug.replace(':publicId', encodeURIComponent(params.publicId || ''));
  return `${localizedPrefix}/${resolved}`.replace(/\/+/g, '/');
}
